<template>
  <section>
    <div id="graph-container" class="px-[20px] relative">
      <!-- Chart Explanation Button -->
      <ExplainButton
        v-if="chart && !loading && !hideExplainButton"
        :chart="chart"
        :context="chartContext"
        @error="handleExplanationError" />
      <div
        id="canvas-container"
        class="w-full h-[250px] relative z-0"
        :class="{ 'opacity-50': loading }">
        <canvas id="bar-chart"></canvas>
      </div>
      <div id="bar-legend-container" class="mt-[20px]"></div>
      <RetirementCalculatorChangeAssumptions class="mt-4" v-if="isRetirementCalculator" />
      <button
        v-else-if="!loading"
        class="text-blue-1 px-3 py-2 button-2 inline-flex items-center gap-x-1.5 mt-2"
        @click="handleModalPopUp">
        <ArrowRightIcon class="text-grey-2 h-5 w-5" />
        <span>How we worked this out</span>
      </button>
      <div
        v-show="loading"
        class="rounded flex flex-row justify-center items-center absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        <BeatLoader color="#0064FF" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { Chart, ChartConfiguration } from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'
import { abbrNum, formatCurrency, isItMobile } from '@/lib/helpers'
import {
  IncomeProjectionChartData,
  IncomeProjectionRawData,
  IncomeProjectionUserMetadata,
  IncomeType,
} from '@/components/ChartJS/IncomeProjection.type.ts'
import { useDebounceFn } from '@vueuse/core'
import BeatLoader from 'vue-spinner/src/PulseLoader.vue'
import HowWeWorkedOutIncomeProjectionModal from '@/components/Modals/HowWeWorkedOutIncomeProjectionModal.vue'
import RetirementCalculatorChangeAssumptions from '@/components/CFS/RetirementCalculatorComponents/RetirementCalculatorChangeAssumptions.vue'
import ExplainButton from '@/components/Charts/ExplainButton.vue'
import type { ChartContext } from '@/types/chartExplanation'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { useModalStore } from '@/store/pinia/ModalStore.ts'

Chart.register(annotationPlugin)

type Props = {
  data: Array<IncomeProjectionChartData>
  raw: Record<IncomeType, Array<IncomeProjectionRawData>>
  userMetadata: IncomeProjectionUserMetadata
  desiredRetirementSalary: number
  xAxis: Array<number>
  loading: boolean
  hideExplainButton?: boolean
  isRetirementCalculator?: boolean
}
const props = defineProps<Props>()
const chart = ref<Chart>()
const TOOLTIP_HEIGHT = 135
const TOOLTIP_WIDTH = 200
const caretDisplayProp = ref('none')
const lineDash = ref<[number, number]>([4, 4])
const gridUpperLimit = ref(0)

const modalStore = useModalStore()

const handleModalPopUp = () => {
  modalStore.openModal(HowWeWorkedOutIncomeProjectionModal, {})
}

const userRetirementLine = computed(() => {
  if (!props.userMetadata?.userFirstName) return []

  const userRetAge = props.userMetadata.userRetirementAge
  const user = props.raw.salary.find((entity) => entity.age == userRetAge)
  const xValue = user?.year - props.raw.salary[0]?.year
  if (!xValue) return []

  // Determine position based on proximity to chart edges
  const chartWidth = props.xAxis.length
  const isNearStart = xValue < chartWidth * 0.3
  const labelPosition = isNearStart ? 'start' : 'end'

  return [
    {
      drawTime: 'afterDatasetsDraw',
      type: 'box',
      xMin: xValue,
      xMax: xValue,
      backgroundColor: 'transparent',
      borderColor: '#70D6FF',
      borderDash: [4, 4],
      z: 90,
    },
    {
      drawTime: 'afterDatasetsDraw',
      type: 'label',
      xValue: xValue,
      yAdjust: -60,
      position: labelPosition,
      backgroundColor: 'rgba(255,255,255,0.60)',
      content: [`${props.userMetadata.userFirstName.toUpperCase()} RETIRES`],
      font: {
        family: 'Raleway',
        size: 12,
        weight: 700,
      },
      z: 99,
    },
  ]
})

const partnerRetirementLine = computed(() => {
  if (!props.userMetadata?.partnerFirstName || !props.userMetadata?.partnerRetirementAge) return []

  const partnerRetAge = props.userMetadata.partnerRetirementAge
  // Find where this retirement age appears on our x-axis (which represents ages)
  const partner = props.raw.partnerSalary?.find((entity) => entity.age == partnerRetAge)
  const xValue = partner?.year - props.raw.salary[0]?.year
  if (!xValue) return []

  // Determine position based on proximity to chart edges
  const chartWidth = props.xAxis.length
  const isNearStart = xValue < chartWidth * 0.3
  const labelPosition = isNearStart ? 'start' : 'end'

  return [
    {
      type: 'box',
      xMin: xValue,
      xMax: xValue,
      backgroundColor: 'transparent',
      borderColor: '#70D6FF',
      borderDash: [4, 4],
      z: 90,
    },
    {
      type: 'label',
      xValue: xValue,
      yAdjust: -30,
      position: labelPosition,
      backgroundColor: 'rgba(255,255,255,0.60)',
      content: [`${props.userMetadata.partnerFirstName.toUpperCase()} RETIRES`],
      font: {
        family: 'Raleway',
        size: 12,
        weight: 700,
      },
      z: 99,
    },
  ]
})

const desiredRetirementIncomeLine = computed(() => {
  return {
    type: 'line',
    yMin: props.desiredRetirementSalary,
    yMax: props.desiredRetirementSalary,
    borderColor: '#ff0000',
    borderWidth: 4,
    borderDash: [8],
    z: 99,
  }
})

// Chart context for better explanations
const chartContext = computed<ChartContext>(() => ({
  chartTitle: 'Income Projection',
  currentAge: props.userMetadata?.userAge,
  retirementAge: props.userMetadata?.userRetirementAge,
  timeframe: `${props.xAxis[0]} - ${props.xAxis[props.xAxis.length - 1]} years`,
  calculationType: 'Retirement income projection',
  chartDescription:
    'Income projections showing salary, super, pension and other income sources over time.',
}))

// Handle explanation error
const handleExplanationError = (error: string) => {
  console.error('Chart explanation error:', error)
  // Could show a toast notification here if needed
}

const setupGraph = () => {
  const data = {
    labels: props.xAxis,
    datasets: [
      ...mapColor(props.data),
      {
        label: 'Desired retirement income',
        data: Array.from({ length: props.xAxis.length }, () => props.desiredRetirementSalary),
        fill: false,
        borderColor: 'transparent',
        borderDash: lineDash.value,
        type: 'line',
        pointStyle: 'dash',
        borderWidth: 1.5,
        order: 0,
      },
    ],
  }
  setGridUpperLimit()
  return {
    type: 'bar',
    data: data,
    options: {
      maintainAspectRatio: false,
      layout: {
        padding: 0,
      },
      interaction: {
        intersect: false,
        mode: 'nearest',
        axis: 'xy',
      },
      // animations: {
      //   tension: {
      //     duration: 1000,
      //     easing: 'linear',
      //     from: 1,
      //     to: 0,
      //     loop: true
      //   }
      // },
      scales: {
        x: {
          border: {
            display: false,
          },
          ticks: {
            maxTicksLimit: props.xAxis.length,
          },
          grid: {
            display: false,
          },
          stacked: true,
          title: {
            display: true,
            text: 'Age',
            color: '#0064FF',
          },
        },
        y: {
          border: {
            display: false,
          },
          ticks: {
            padding: 5,
            maxTicksLimit: 4,
            callback: function (value: number) {
              return value ? '$' + abbrNum(value) : '$0'
            },
          },
          grid: {
            color: function (context) {
              return context.tick.value < gridUpperLimit.value ? '#70D6FF' : 'transparent'
            },
          },
          stacked: true,
          title: {
            display: true,
            text: 'Income',
            color: '#0064FF',
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: externalTooltipHandler,
        },
        htmlLegend: {
          containerID: 'bar-legend-container',
        },
        legend: {
          display: false,
          position: 'bottom',
          align: 'center',
        },
        annotation: {
          annotations: [
            desiredRetirementIncomeLine.value,
            ...userRetirementLine.value,
            ...partnerRetirementLine.value,
          ],
        },
      },
    },
    plugins: [htmlLegendPlugin],
  } as unknown as ChartConfiguration
}

const firstDraw = ref(true)
const drawGraph = useDebounceFn(() => {
  // If chart exists and we have data, update instead of recreating
  if (chart.value && props.data.length > 0) {
    console.log('Chart exists, updating data instead of recreating')
    updateData(props.data)
    return
  }

  // Only create new chart if none exists
  console.log('Creating new chart instance')
  const config = setupGraph()
  let canvas: HTMLCanvasElement
  nextTick(async () => {
    canvas = document.getElementById('bar-chart') as HTMLCanvasElement
  }).then(() => {
    chart.value = new Chart(canvas, config)
    firstDraw.value = false
  })
}, 300)

const setGridUpperLimit = () => {
  const sumArr = new Array(props.xAxis.length).fill(0)
  props.data.forEach((el) => {
    el.data.forEach((value, index) => {
      sumArr[index] += value
    })
  })
  gridUpperLimit.value = Math.max(...sumArr)
}

const getOrCreateLegendList = (id: string) => {
  const legendContainer = document.getElementById(id)
  if (!legendContainer) {
    return console.warn('Null HTMLElement found')
  }
  let listContainer = legendContainer.querySelector('div')

  if (!listContainer) {
    listContainer = document.createElement('div')
    listContainer.style.display = 'flex'
    listContainer.style.flexDirection = 'row'
    listContainer.style.flexWrap = 'wrap'
    listContainer.style.justifyContent = 'center'
    listContainer.style.gap = '5px'
    legendContainer.appendChild(listContainer)
  }

  return listContainer
}
const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div')

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.style.background = 'rgba(255, 255, 255, 0.9)'
    tooltipEl.style.borderRadius = '12px'
    tooltipEl.style.color = '#000'
    tooltipEl.style.opacity = 1
    tooltipEl.style.padding = '10px'
    tooltipEl.style.pointerEvents = 'none'
    tooltipEl.style.position = 'absolute'
    tooltipEl.style.transform = 'translate(-50%, 0)'
    tooltipEl.style.transition = 'all .1s ease'
    tooltipEl.style.transition = 'all .1s ease'
    tooltipEl.classList.add('bar-tooltip')

    const table = document.createElement('table')
    table.style.margin = '0px'
    table.style.width = TOOLTIP_WIDTH + 'px'
    table.style.height = 'auto'
    table.style.borderCollapse = 'collapse'

    tooltipEl.appendChild(table)
    chart.canvas.parentNode.appendChild(tooltipEl)
  }
  return tooltipEl
}

const externalTooltipHandler = ({ chart, tooltip }) => {
  // Tooltip Element
  const isLineChart = tooltip.dataPoints[0].dataset.type === 'line'
  const tooltipEl = getOrCreateTooltip(chart)
  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }

  // Set Text
  if (tooltip.body) {
    const tableBody = document.createElement('tbody')

    if (isLineChart) {
      const tr = addRow()
      const td = addLabel(
        'Desired retirement income: ',
        formatCurrency(props.desiredRetirementSalary) || ''
      )
      tr.appendChild(td)
      tableBody.appendChild(tr)
    } else {
      const found = props.raw.salary.find((entity) => entity.age == +tooltip.title[0])
      if (!found) {
        return
      }
      const tr = addRow()
      const td = addLabel('Year ', found.year.toString())
      tr.appendChild(td)
      tableBody.appendChild(tr)

      addUserName(tableBody)
      addUserAge(tableBody, +tooltip.title[0])
      addUserSalary(tableBody, +tooltip.title[0])
      addUserPension(tableBody, +tooltip.title[0])
      addUserSuper(tableBody, +tooltip.title[0])
      addProperty(tableBody, +tooltip.title[0])
      addCashAndFixed(tableBody, +tooltip.title[0])
      addShares(tableBody, +tooltip.title[0])
      addManagedFunds(tableBody, +tooltip.title[0])
      addAlternative(tableBody, +tooltip.title[0])
      // addIncomeGap(tableBody, +tooltip.title[0]) nathan says NO
    }

    const tableRoot = tooltipEl.querySelector('table')

    // Remove old children if tableRoot exists
    if (tableRoot && tableRoot.firstChild) {
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove()
      }
    }

    // Add new children
    if (tableRoot) {
      tableRoot.appendChild(tableBody)
    } else {
      const table = document.createElement('table')
      table.appendChild(tableBody)
      tooltipEl.appendChild(table)
    }
  }

  const chartRighthandBound: number = Math.round(chart.chartArea.right)
  const outsideBounds = chartRighthandBound - (TOOLTIP_WIDTH + 15) <= tooltip.caretX

  const topOffset = isLineChart ? 20 : TOOLTIP_HEIGHT / 2
  caretDisplayProp.value = isLineChart ? 'none' : 'block'
  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1
  tooltipEl.style.position = 'absolute'
  tooltipEl.style.left = outsideBounds
    ? chartRighthandBound - (TOOLTIP_WIDTH / 2 + 7) + 'px'
    : TOOLTIP_WIDTH / 2 + 15 + tooltip.caretX + 'px'
  tooltipEl.style.top = -topOffset + tooltip.caretY + 'px'
  tooltipEl.style.font = tooltip.options.bodyFont.string
  tooltipEl.style.pointerEvents = 'none'
  tooltipEl.style.zIndex = '50'
}

const addColumn = (text: string, textColor: string, width?: string, fontWeight?: string) => {
  const td = document.createElement('td')
  td.style.borderWidth = '0'
  td.style.width = width || '40px'
  td.style.fontSize = '10px'
  const tdSpan = document.createElement('span')
  tdSpan.style.color = textColor
  if (fontWeight) {
    tdSpan.style.fontWeight = fontWeight
  }
  td.appendChild(tdSpan)
  const tdSpanText = document.createTextNode(text)
  tdSpan.appendChild(tdSpanText)
  td.appendChild(tdSpan)
  return td
}

const addLabel = (firstText: string, secondText?: string) => {
  const td = document.createElement('td')
  td.style.borderWidth = '0'
  td.style.fontSize = '10px'
  td.style.color = '#0064FF'

  const span = document.createElement('span')
  span.style.fontWeight = '900'
  const text = document.createTextNode(firstText)
  span.appendChild(text)
  if (secondText) {
    const bold = document.createElement('bold')
    bold.style.fontWeight = '900'
    bold.style.color = '#0037A1'
    const titleText = document.createTextNode(secondText)
    bold.appendChild(titleText)
    span.appendChild(bold)
  }

  td.appendChild(span)
  return td
}

const addRow = () => {
  const tr = document.createElement('tr')
  tr.style.backgroundColor = 'inherit'
  tr.style.borderWidth = '0'
  return tr
}
// @TODO: refactor this, prob run through the array.
const addUserName = (tableBody: HTMLTableSectionElement) => {
  const tr = addRow()
  const td1 = addColumn('', '#0037A1', '80px')
  tr.appendChild(td1)

  const td2 = addColumn(props.userMetadata.userFirstName, '#0037A1', '40px', '900')
  tr.appendChild(td2)

  if (props.userMetadata.partnerFirstName) {
    const td = addColumn(props.userMetadata.partnerFirstName, '#0037A1', '40px', '900')
    tr.appendChild(td)
  }

  tableBody.appendChild(tr)
}

const addUserAge = (tableBody: HTMLTableSectionElement, xAxisTitle: number) => {
  const tr = addRow()
  const td1 = addColumn('AGE', '#0037A1', '80px', '900')
  tr.appendChild(td1)
  const td2 = addColumn(xAxisTitle.toString(), '#0064FF')
  tr.appendChild(td2)

  if (props.userMetadata.partnerFirstName) {
    if (props.raw.partnerSalary) {
      const index = props.raw.salary.findIndex((entity) => entity.age == xAxisTitle)
      const found = props.raw.partnerSalary[index]
      if (found) {
        const td = addColumn(found.age.toString(), '#0064FF')
        tr.appendChild(td)
      } else {
        const td = addColumn('0', '#0064FF')
        tr.appendChild(td)
      }
    } else {
      const td = addColumn('0', '#0064FF')
      tr.appendChild(td)
    }
  }

  tableBody.appendChild(tr)
}

const addUserSalary = (tableBody: HTMLTableSectionElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('SALARY', '#0037A1', '80px', '900')
  tr.appendChild(td)

  if (props.raw.salary) {
    const found = props.raw.salary.find((entity) => entity.age == xAxisTitle)
    if (found) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
    } else {
      const td = addColumn('$0', '#0064FF')
      tr.appendChild(td)
    }
  } else {
    const td = addColumn('$0', '#0064FF')
    tr.appendChild(td)
  }

  if (props.userMetadata.partnerFirstName) {
    if (props.raw.partnerSalary) {
      const index = props.raw.salary.findIndex((entity) => entity.age == xAxisTitle)
      const found = props.raw.partnerSalary[index]
      if (found) {
        const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
        tr.appendChild(td)
      } else {
        const td = addColumn('$0', '#0064FF')
        tr.appendChild(td)
      }
    } else {
      const td = addColumn('$0', '#0064FF')
      tr.appendChild(td)
    }
  }

  tableBody.appendChild(tr)
}

const addUserPension = (tableBody: HTMLTableSectionElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('PENSION', '#0037A1', '80px', '900')
  tr.appendChild(td)

  let hasAnyPensionData = false

  if (props.raw.pension) {
    const found = props.raw.pension.find((entity) => entity.age == xAxisTitle)
    if (found && found.projection > 0) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
      hasAnyPensionData = true
    } else {
      const td = addColumn('$0', '#0064FF')
      tr.appendChild(td)
    }
  } else {
    const td = addColumn('$0', '#0064FF')
    tr.appendChild(td)
  }

  if (props.userMetadata.partnerFirstName) {
    if (props.raw.partnerPension) {
      const index = props.raw.pension.findIndex((entity) => entity.age == xAxisTitle)
      const found = props.raw.partnerPension[index]
      if (found && found.projection > 0) {
        const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
        tr.appendChild(td)
        hasAnyPensionData = true
      } else {
        const td = addColumn('$0', '#0064FF')
        tr.appendChild(td)
      }
    } else {
      const td = addColumn('$0', '#0064FF')
      tr.appendChild(td)
    }
  }

  if (hasAnyPensionData) {
    tableBody.appendChild(tr)
  }
}

const addUserSuper = (tableBody: HTMLTableSectionElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('SUPER', '#0037A1', '80px', '900')
  tr.appendChild(td)

  if (props.raw.super) {
    const found = props.raw.super.find((entity) => entity.age == xAxisTitle)
    if (found) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
    } else {
      const td = addColumn('$0', '#0064FF')
      tr.appendChild(td)
    }
  } else {
    const td = addColumn('$0', '#0064FF')
    tr.appendChild(td)
  }

  if (props.userMetadata.partnerFirstName) {
    if (props.raw.partnerSuper) {
      const index = props.raw.super.findIndex((entity) => entity.age == xAxisTitle)
      const found = props.raw.partnerSuper[index]
      if (found) {
        const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
        tr.appendChild(td)
      } else {
        const td = addColumn('$0', '#0064FF')
        tr.appendChild(td)
      }
    } else {
      const td = addColumn('$0', '#0064FF')
      tr.appendChild(td)
    }
  }

  tableBody.appendChild(tr)
}

const addProperty = (tableBody: HTMLTableSectionElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('PROPERTY', '#0037A1', '80px', '900')
  tr.appendChild(td)

  if (props.raw.property) {
    const found = props.raw.property.find((entity) => entity.age == xAxisTitle)
    if (found && found.projection > 0) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
      tableBody.appendChild(tr)
    }
  }
}

const addCashAndFixed = (table: HTMLTableElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('CASH', '#0037A1', '80px', '900')
  tr.appendChild(td)

  if (props.raw.cashAndFixed) {
    const found = props.raw.cashAndFixed.find((entity) => entity.age == xAxisTitle)
    if (found && found.projection > 0) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
      table.appendChild(tr)
    }
  }
}

const addShares = (table: HTMLTableElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('SHARES', '#0037A1', '80px', '900')
  tr.appendChild(td)

  if (props.raw.shares) {
    const found = props.raw.shares.find((entity) => entity.age == xAxisTitle)
    if (found && found.projection > 0) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
      table.appendChild(tr)
    }
  }
}

const addManagedFunds = (table: HTMLTableElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('MANAGED FUNDS', '#0037A1', '80px', '900')
  tr.appendChild(td)
  if (props.raw.managedFunds) {
    const found = props.raw.managedFunds.find((entity) => entity.age == xAxisTitle)
    if (found && found.projection > 0) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
      table.appendChild(tr)
    }
  }
}

const addAlternative = (table: HTMLTableElement, xAxisTitle: number) => {
  const tr = addRow()
  const td = addColumn('ALTERNATIVE', '#0037A1', '80px', '900')
  tr.appendChild(td)

  if (props.raw.alternative) {
    const found = props.raw.alternative.find((entity) => entity.age == xAxisTitle)
    if (found && found.projection > 0) {
      const td = addColumn(formatCurrency(found.projection) || '', '#0064FF')
      tr.appendChild(td)
      table.appendChild(tr)
    }
  }
}

const addIncomeGap = (tableBody: HTMLTableSectionElement, xAxisTitle: number) => {
  const tr = addRow()

  const userSuper = props.raw.super.find((entity) => entity.age == xAxisTitle)?.projection || 0
  const salary = props.raw.salary.find((entity) => entity.age == xAxisTitle)?.projection || 0
  const pension = props.raw.pension.find((entity) => entity.age == xAxisTitle)?.projection || 0
  const property = props.raw.property?.find((entity) => entity.age == xAxisTitle)?.projection || 0
  const cashAndFixed =
    props.raw.cashAndFixed?.find((entity) => entity.age == xAxisTitle)?.projection || 0
  const shares = props.raw.shares?.find((entity) => entity.age == xAxisTitle)?.projection || 0
  const alternative =
    props.raw.alternative?.find((entity) => entity.age == xAxisTitle)?.projection || 0
  let incomeGap =
    props.desiredRetirementSalary -
    (userSuper + salary + pension + property + cashAndFixed + shares + alternative)
  if (incomeGap <= 0) return

  const td = addColumn('INCOME GAP', '#000032', '80px', '900')
  tr.appendChild(td)
  const td1 = addColumn(formatCurrency(incomeGap) || '', '#000032', '40px', '900')
  tr.appendChild(td1)

  if (props.userMetadata.partnerFirstName) {
    const index = props.raw.super.findIndex((entity) => entity.age == xAxisTitle)
    const partnerSuper = props.raw.partnerSuper ? props.raw.partnerSuper[index].projection : 0
    const partnerPension = props.raw.partnerPension ? props.raw.partnerPension[index].projection : 0
    const partnerSalary = props.raw.partnerSalary ? props.raw.partnerSalary[index].projection : 0
    incomeGap =
      props.desiredRetirementSalary -
      (userSuper +
        salary +
        pension +
        property +
        cashAndFixed +
        shares +
        alternative +
        partnerSuper +
        partnerPension +
        partnerSalary)
    if (incomeGap <= 0) return
    const td = addColumn(formatCurrency(incomeGap) || '', '#000032', '40px', '900')
    tr.appendChild(td)
  }

  tableBody.appendChild(tr)
}

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const div = getOrCreateLegendList(options.containerID)
    if (!div) {
      console.warn('LegendList not found')
      return null
    }
    // Remove old legend items
    while (div.firstChild) {
      div.firstChild.remove()
    }

    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart)
    items.forEach((item) => {
      if (!item.lineDash) {
        const legendItem = document.createElement('div')
        legendItem.style.alignItems = 'center'
        legendItem.style.cursor = 'pointer'
        legendItem.style.display = 'flex'
        legendItem.style.flexDirection = 'row'
        legendItem.style.fontFamily = 'Raleway'
        legendItem.style.flexWrap = 'wrap'
        legendItem.style.padding = '5px'
        legendItem.style.borderRadius = '3px'
        legendItem.style.border = '1px solid #EEEBEB'

        if (item.hidden) {
          // disabled style
          legendItem.style.background = 'white'
          legendItem.style.borderLeft = `3px solid ${item.fillStyle}`
        } else {
          // enabled style
          legendItem.style.background = item.fillStyle
          legendItem.style.borderLeft = `3px solid ${item.fillStyle}`
        }

        legendItem.onclick = () => {
          const { type } = chart.config
          if (type === 'pie' || type === 'doughnut') {
            // Pie and doughnut charts only have a single dataset and visibility is per item
            chart.toggleDataVisibility(item.index)
          } else {
            chart.setDatasetVisibility(
              item.datasetIndex,
              !chart.isDatasetVisible(item.datasetIndex)
            )
          }
          chart.update()
        }

        // Text
        const textContainer = document.createElement('p')
        textContainer.style.color = item.hidden ? '#888' : '#fff'
        textContainer.style.fontSize = '10px'
        textContainer.style.textTransform = 'uppercase'
        textContainer.style.fontFamily = 'Raleway'
        textContainer.style.fontWeight = 'bold'
        textContainer.style.margin = '0'
        textContainer.style.padding = '0'
        const text = document.createTextNode(item.text)
        textContainer.appendChild(text)
        legendItem.appendChild(textContainer)
        div.appendChild(legendItem)
      }
    })
  },
}

const mapColor = (dataset: Array<Record<string, unknown>>) => {
  const colors = {
    Salary: '#0037A1',
    Super: '#00885E',
    Pension: '#F00',
    'Partner Salary': '#0064FF',
    'Partner Super': '#01CC9B',
    'Partner Pension': '#982c17',
    Shares: '#fd8900',
    'Managed Funds': '#d70181',
    Property: '#000032',
    Alternative: '#696969',
    Cash: '#32CD32', // Match CFS Cash color from data processor
  }

  return dataset.map((value) => {
    value.backgroundColor = colors[value.label] || '#A0A0A0'
    return value
  })
}

const updateData = (newData: IncomeProjectionChartData[]) => {
  console.log('IncomeProjectionChart updateData called with:', newData?.length, 'datasets')
  newData?.forEach((dataset, index) => {
    console.log(
      `  Dataset ${index}: ${dataset.label} (${dataset.data?.length} points, first 3: [${dataset.data?.slice(0, 3).join(', ')}...])`
    )
  })

  const instance = Object.values(Chart.instances)
    .filter((c) => c.canvas.id === 'bar-chart')
    .pop() as Chart

  // Exit early if chart instance doesn't exist yet
  if (!instance || !instance.data || !instance.data.datasets) {
    console.log('Chart instance not ready, skipping update')
    return
  }

  // Replace all datasets except the retirement income line
  const retirementIncomeDataset = instance.data.datasets.find(
    (d) => d.label === 'Desired retirement income'
  )
  const mappedNewData = mapColor(newData)

  // Replace all datasets with new ones, preserving retirement income line
  instance.data.datasets = [...mappedNewData, retirementIncomeDataset].filter(Boolean)

  // update retirement line annotations
  instance.options.plugins.annotation.annotations = [
    desiredRetirementIncomeLine.value,
    ...userRetirementLine.value,
    ...partnerRetirementLine.value,
  ]

  instance.update()
}

watch(
  () => props.data,
  (data, oldData) => {
    if (JSON.stringify(data) === JSON.stringify(oldData)) return
    if (chart.value) {
      updateData(data)
    } else if (data.length > 0 && !props.loading) {
      // If chart doesn't exist but we have data and not loading, create the chart
      console.log('Creating chart instance as data arrived before chart was initialized')
      drawGraph()
    }
  },
  { deep: true, immediate: true }
)

watch(
  () => props.loading,
  (loading, oldValue) => {
    if (!loading && oldValue && firstDraw.value) drawGraph()
  }
)

onBeforeMount(() => {
  if (isItMobile()) {
    lineDash.value = [1.375, 1.375]
  }
})

onBeforeUnmount(() => {
  // cleanup
  const chart = Chart.getChart('bar-chart')
  chart?.destroy()
})
</script>

<style>
.bar-tooltip:before {
  display: v-bind(caretDisplayProp);
  content: ' ';
  position: absolute;
  left: -15px;
  top: 65px;
  border: 8px solid transparent;
  border-right-color: rgba(255, 255, 255, 0.9);
}
</style>
